import CmsBlockFactory from "@/helpers/editor/blocks/CmsBlockFactory.js";
import Vue from 'vue';
import App from './App.vue';
import router from './router.js';
import store from './store.js';
import "@vue-components/helpers/vue-mixin.js";
import './vue-global';
import '../vue-components/helpers/vue-filters.js';
import VHotkey from 'v-hotkey';
import VueIziToast from 'vue-izitoast'
import i18n, { loadLanguageAsync } from '@vue-components/plugins/I18nPlugin.js';
import { getLocale, vueI18n, tr } from '@vue-components/helpers/I18nPluginHelper.js';
import {Liquid} from "liquidjs";

// Dynamically load moment locale based on current language
function loadMomentLocale(locale) {
    const shortLocale = locale?.split(`-`)[0] || `en`;

    // English is the default, no need to import
    if (shortLocale === `en`) {
        return Promise.resolve();
    }

    // Dynamically import the locale if it exists
    return import(`moment/locale/${shortLocale}`)
        .catch(err => {
            console.warn(`Failed to load moment locale: ${shortLocale}`, err);
        });
}

// Load initial moment locale
const initialLocale = getLocale();
loadMomentLocale(initialLocale);

import {
    AlertPlugin,
    BadgePlugin,
    ButtonPlugin,
    ButtonGroupPlugin,
    CardPlugin,
    CollapsePlugin,
    CarouselPlugin,
    DropdownPlugin,
    EmbedPlugin,
    FormPlugin,
    FormInputPlugin,
    FormGroupPlugin,
    FormCheckboxPlugin,
    FormRadioPlugin,
    FormSpinbuttonPlugin,
    FormSelectPlugin,
    FormTagsPlugin,
    FormTextareaPlugin,
    ImagePlugin,
    InputGroupPlugin,
    LayoutPlugin,
    ListGroupPlugin,
    ModalPlugin,
    MediaPlugin,
    NavbarPlugin,
    NavPlugin,
    OverlayPlugin,
    PaginationPlugin,
    PopoverPlugin,
    ProgressPlugin,
    SidebarPlugin,
    TablePlugin,
    TabsPlugin,
    ToastPlugin,
    TooltipPlugin,
    VBModalPlugin
} from 'bootstrap-vue'

// CMS Common Components
import CmsContainer from "@/components/editor/rendering/cms-container.vue";
import CmsCustomCss from "@/components/editor/rendering/cms-custom-css.vue";
import CmsLink from "@/components/cms/cms-link.vue";

import CmsBlock from "@/components/editor/rendering/blocks/cms-block.vue";
import CmsBlockBuilder from "@/components/editor/rendering/blocks/cms-block-builder.vue";
import CmsBlockRenderer from "@/components/editor/rendering/blocks/cms-block-renderer.vue";
import CmsBlockMenu from "@/components/editor/rendering/blocks/cms-block-menu.vue";
import CmsBlockFooter from "@/components/editor/rendering/blocks/cms-block-footer.vue";

import CmsComponent from "@/components/editor/rendering/components/cms-component.vue";
import CmsComponentButton from "@/components/editor/rendering/components/cms-component-button.vue";
import CmsComponentGlyph from "@/components/editor/rendering/components/cms-component-glyph.vue";
import CmsComponentImage from "@/components/editor/rendering/components/cms-component-image.vue";
import CmsComponentNavigationLinks from "@/components/editor/rendering/components/cms-component-navigation-links.vue";
import CmsComponentSocialLinks from "@/components/editor/rendering/components/cms-component-social-links.vue";
import CmsComponentText from "@/components/editor/rendering/components/cms-component-text.vue";
import WebsitePage from '/src/layout/website-page.vue'

import VueMeta from 'vue-meta'
import Vue2TouchEvents from 'vue2-touch-events'
import ClientSideORMProvider from "@vue-components/helpers/ClientSideORMProvider.js";
import './registerServiceWorker'

globalThis.eyeInORMProvider = ClientSideORMProvider;
Vue.config.productionTip = false;

// CMS
Vue.component(`cms-container`, CmsContainer);
Vue.component(`cms-custom-css`, CmsCustomCss);
Vue.component(`cms-link`, CmsLink);

Vue.component(`cms-block`, CmsBlock);
Vue.component(`cms-block-footer`, CmsBlockFooter);
Vue.component(`cms-block-menu`, CmsBlockMenu);
Vue.component(`cms-block-builder`, CmsBlockBuilder);
Vue.component(`cms-block-renderer`, CmsBlockRenderer);

Vue.component(`cms-component`, CmsComponent);
Vue.component(`cms-component-button`, CmsComponentButton);
Vue.component(`cms-component-glyph`, CmsComponentGlyph);
Vue.component(`cms-component-image`, CmsComponentImage);
Vue.component(`cms-component-navigation-links`, CmsComponentNavigationLinks);
Vue.component(`cms-component-social-links`, CmsComponentSocialLinks);
Vue.component(`cms-component-text`, CmsComponentText);

Vue.component(`website-page`, WebsitePage);

// Bootstrap
Vue.use(AlertPlugin);
Vue.use(BadgePlugin);
Vue.use(ButtonPlugin);
Vue.use(ButtonGroupPlugin);
Vue.use(CardPlugin);
Vue.use(CarouselPlugin);
Vue.use(CollapsePlugin);
Vue.use(DropdownPlugin);
Vue.use(EmbedPlugin);
Vue.use(FormPlugin);
Vue.use(FormInputPlugin);
Vue.use(FormGroupPlugin);
Vue.use(FormCheckboxPlugin);
Vue.use(FormRadioPlugin);
Vue.use(FormSpinbuttonPlugin);
Vue.use(FormSelectPlugin);
Vue.use(FormTagsPlugin);
Vue.use(FormTextareaPlugin);
Vue.use(ImagePlugin);
Vue.use(InputGroupPlugin);
Vue.use(LayoutPlugin);
Vue.use(ListGroupPlugin);
Vue.use(ModalPlugin);
Vue.use(MediaPlugin);
Vue.use(NavbarPlugin);
Vue.use(NavPlugin);
Vue.use(OverlayPlugin);
Vue.use(PaginationPlugin);
Vue.use(PopoverPlugin);
Vue.use(ProgressPlugin);
Vue.use(SidebarPlugin);
Vue.use(TablePlugin);
Vue.use(TabsPlugin);
Vue.use(ToastPlugin);
Vue.use(TooltipPlugin);
Vue.use(VBModalPlugin);

Vue.use(VueIziToast);

Vue.use(Vue2TouchEvents, {
    touchClass: ``,
    tapTolerance: 10,
    swipeTolerance: 30,
    longTapTimeInterval: 400
});
Vue.use(VueMeta);
Vue.use(VHotkey);


window.cmsFactory = new CmsBlockFactory();
window.liquid = new Liquid();
window.liquid.registerFilter('tr', tr);

function mountApp() {
    new Vue({
        router,
        store,
        i18n,
        render: (h) => h(App),
    }).$mount(`#app`);
}

loadLanguageAsync(initialLocale)
    .catch((error) => {
        console.warn(`Initial language preload failed`, error);
    })
    .finally(() => {
        mountApp();
    });
